/**
 * Puente entre el tutorial y la pantalla.
 *
 * El motor no habla con React: mira el DOM. Así el tutorial puede comprobar de
 * verdad lo que ha hecho el usuario (que la ficha está abierta, que hay dos
 * aristas en el lienzo, que el acceso quedó guardado) sin tener que engancharse
 * a los componentes de cada página.
 *
 * Convenios del campo `target` de un paso:
 *   '.clase'            selector CSS normal
 *   '@text=Añadir CI'   el primer botón/enlace/pestaña que contiene ese texto
 *   null                no hay nada que resaltar: burbuja centrada
 */

const CLICKABLE = 'button, a, [role="button"], .dv-tab-btn, .sd-add-btn, .wol-btn-primary';

/** ¿Está el elemento realmente en pantalla? (los modales ocultan el fondo) */
export function isVisible(el) {
  if (!el) return false;
  if (typeof el.getClientRects === 'function' && el.getClientRects().length === 0) return false;
  return el.offsetParent !== null || getComputedStyle(el).position === 'fixed';
}

/** Entrada del menú lateral que lleva a esa ruta (aunque el grupo esté cerrado). */
export function findMenuItemLink(path) {
  if (!path) return null;
  const clean = path.split('?')[0];
  const links = [...document.querySelectorAll('a.sidebar-link, .sidebar a, .sidebar-link')];
  return links.find(a => (a.getAttribute('href') || '') === clean)
    || links.find(a => (a.getAttribute('href') || '').startsWith(clean)) || null;
}

/** Botones de grupo del menú lateral, marcando los que están cerrados. */
export function findSidebarToggles() {
  return [...document.querySelectorAll('.sidebar-accordion-toggle')].map(btn => ({
    el: btn,
    abierto: /active|open|expanded/.test(String(btn.className || '')) || btn.getAttribute('aria-expanded') === 'true',
    text: (btn.textContent || '').trim(),
  }));
}

export function findTarget(spec) {
  if (!spec) return null;
  try {
    if (spec.startsWith('@text=')) {
      const needle = spec.slice(6).trim().toLowerCase();
      // textContent y no innerText: así encuentra también botones cuyo
      // contenedor está oculto en ese momento. Y se prefiere uno visible.
      const all = [...document.querySelectorAll(CLICKABLE)];
      const match = (el) => (el.textContent || '').trim().toLowerCase().includes(needle);
      return all.find(el => match(el) && isVisible(el)) || all.find(match) || null;
    }
    return document.querySelector(spec);
  } catch {
    return null;
  }
}

function numeric(text) {
  const n = parseInt(String(text).replace(/[^\d-]/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Cuenta del botón de la barra de smart buttons («Accesos», «Docs»...).
 * El valor va en `.dv-smart-btn-value` y la etiqueta en `.dv-smart-btn-label`.
 */
function smartCount(label) {
  const wanted = label.trim().toLowerCase();
  const btns = [...document.querySelectorAll('.dv-smart-btn')];
  const hit = btns.find(btn => {
    const lbl = btn.querySelector('.dv-smart-btn-label');
    return lbl && (lbl.textContent || '').trim().toLowerCase() === wanted;
  });
  if (!hit) return 0;
  const val = hit.querySelector('.dv-smart-btn-value');
  return val ? numeric(val.textContent) : 0;
}

/** Fila del catálogo de servicios cuyo texto contiene `name` (devuelve el id). */
function serviceRowId(name) {
  const needle = name.trim().toLowerCase();
  const rows = [...document.querySelectorAll('tr[id^="svc-row-"]')];
  // textContent: la tabla del catálogo queda oculta (`display:none`) mientras
  // la ficha está abierta, y `innerText` devolvería cadena vacía ahí.
  const matches = rows.filter(r => (r.textContent || '').toLowerCase().includes(needle));
  if (!matches.length) return null;
  // Si alguien repite el tutorial y hay dos con el mismo nombre, el bueno es el
  // último creado (id más alto).
  const newest = matches.sort((a, b) =>
    Number(b.id.replace('svc-row-', '')) - Number(a.id.replace('svc-row-', '')))[0];
  return numeric(newest.id.replace('svc-row-', '')) || null;
}

export function createCtx() {
  return {
    findTarget,
    isVisible,

    has: (sel) => !!findTarget(sel),
    count: (sel) => {
      try { return document.querySelectorAll(sel).length; } catch { return 0; }
    },
    text: (sel) => {
      const el = findTarget(sel);
      return el ? (el.innerText || '').trim() : '';
    },
    value: (sel) => {
      const el = document.querySelector(sel);
      return el && typeof el.value === 'string' ? el.value : '';
    },

    /** Aristas dibujadas en el lienzo del Diseñador (o en el mapa de servicios). */
    edgesCount: () => document.querySelectorAll('.sd-canvas svg path[marker-end]').length,

    /** Equipos (CIs) asociados al servicio en la ficha. */
    hostRows: () => document.querySelectorAll('.svc-hosts-list > div').length,

    smartCount,

    /** ¿Existe ya un servicio en el catálogo con ese nombre visible? */
    hasServiceNamed: (name) => serviceRowId(name) !== null,
    serviceRowId,
  };
}
